/** Sidebar menu — a single-open accordion. Entries are declared once (with an
 * optional perm) inside labeled sections; both SECTIONS and GROUPS open one at a
 * time (opening one closes its siblings), which caps the rail's height at roughly
 * one open section regardless of how many destinations exist. Navigating opens
 * the section + group holding the active route. In `mini` mode the menu renders
 * as an icon-only rail: labels move into tooltips, section headers become
 * dividers, and clicking a group icon re-opens the full drawer with that group
 * expanded. The full nav is always reachable via the ⌘K palette (it flattens
 * SECTIONS), so the rail does not need to show everything at once. Server is
 * always the authoritative permission check.
 */
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import BadgeIcon from '@mui/icons-material/Badge';
import HandshakeIcon from '@mui/icons-material/Handshake';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import BlockIcon from '@mui/icons-material/Block';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import CategoryIcon from '@mui/icons-material/Category';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WebhookIcon from '@mui/icons-material/Webhook';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ExploreIcon from '@mui/icons-material/Explore';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import PaidIcon from '@mui/icons-material/Paid';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LinkIcon from '@mui/icons-material/Link';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RuleIcon from '@mui/icons-material/Rule';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SyncIcon from '@mui/icons-material/Sync';
import TourIcon from '@mui/icons-material/Tour';
import TimelineIcon from '@mui/icons-material/Timeline';
import TranslateIcon from '@mui/icons-material/Translate';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import TuneIcon from '@mui/icons-material/Tune';
import KeyIcon from '@mui/icons-material/Key';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LuggageIcon from '@mui/icons-material/Luggage';
import MapIcon from '@mui/icons-material/Map';
import PaletteIcon from '@mui/icons-material/Palette';
import PeopleIcon from '@mui/icons-material/People';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import PreviewIcon from '@mui/icons-material/Preview';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import PublicIcon from '@mui/icons-material/Public';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import RouteIcon from '@mui/icons-material/Route';
import PolylineIcon from '@mui/icons-material/Polyline';
import SchemaIcon from '@mui/icons-material/Schema';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SecurityIcon from '@mui/icons-material/Security';
import SellIcon from '@mui/icons-material/Sell';
import ShieldIcon from '@mui/icons-material/Shield';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import HubIcon from '@mui/icons-material/Hub';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CampaignIcon from '@mui/icons-material/Campaign';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import type { SvgIconComponent } from '@mui/icons-material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { usePermissions } from '@/hooks/usePermissions';

export interface Item {
  id: string;
  label: string;
  to: string;
  icon: SvgIconComponent;
  perm?: string;
  /** NavLink `end` matching — set on entries whose path is a prefix of
   *  sibling entries (e.g. /notifications vs /notifications/blocks) so only
   *  the exact route highlights. */
  end?: boolean;
}

export interface Group {
  id: string;
  label: string;
  icon: SvgIconComponent;
  children: Item[];
}

export type Entry = Item | Group;

export interface Section {
  id: string;
  label: string;
  entries: Entry[];
}

export const isGroup = (e: Entry): e is Group => 'children' in e;

/** The navigation source of truth — the sidebar renders it and the ⌘K
 *  command palette flattens it into jump targets.
 *
 *  Three sections, single-open. Groups hold only leaves (depth 2). Where a
 *  cluster naturally sub-groups (Catalog), it does — so no section is a flat
 *  wall of links. Section/group membership is pure data: re-home an entry by
 *  moving its object; ⌘K and the rail update together. */
export const SECTIONS: Section[] = [
  {
    id: 'directory',
    label: 'Directory',
    entries: [
      {
        id: 'users',
        label: 'Users',
        icon: PeopleIcon,
        children: [
          { id: 'users-system', label: 'System Users', to: '/users/system', icon: AdminPanelSettingsIcon, perm: 'console_user_view' },
          { id: 'users-staff', label: 'Staff', to: '/users/staff', icon: BadgeIcon, perm: 'console_user_view' },
          { id: 'users-guests', label: 'Guests', to: '/users/guests', icon: LuggageIcon, perm: 'console_user_view' },
          { id: 'users-sessions', label: 'Sessions', to: '/users/sessions', icon: KeyIcon, perm: 'console_session_view' },
        ],
      },
      { id: 'tour-operators', label: 'Tour Operators', to: '/tour-operators', icon: AccountTreeIcon, perm: 'console_top_view' },
      { id: 'onboarding-invites', label: 'Onboarding Invites', to: '/onboarding-invites', icon: QrCode2Icon, perm: 'console_onboarding_invites_view' },
      { id: 'tenant-requests', label: 'Tenant Requests', to: '/tenant-requests', icon: PendingActionsIcon, perm: 'console_top_manage' },
      { id: 'top-batch-config', label: 'Batch Config', to: '/tour-operators/configs', icon: TuneIcon, perm: 'console_top_view' },
      {
        // RBAC lives with the people it governs (was its own single-group section).
        id: 'rbac',
        label: 'RBAC',
        icon: ShieldIcon,
        children: [
          { id: 'rbac-template-groups', label: 'Template Groups', to: '/rbac/template-groups', icon: LibraryBooksIcon, perm: 'console_rbac_view' },
          { id: 'rbac-templates', label: 'Templates', to: '/rbac/templates', icon: WorkspacesIcon, perm: 'console_rbac_view' },
          { id: 'rbac-access-groups', label: 'Access Groups', to: '/rbac/access-groups', icon: SecurityIcon, perm: 'console_rbac_view' },
        ],
      },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    entries: [
      {
        id: 'trips',
        label: 'Trips',
        icon: ExploreIcon,
        children: [
          { id: 'tours', label: 'Tours', to: '/tours', icon: TourIcon, perm: 'console_tours_view' },
          { id: 'itineraries', label: 'Itineraries', to: '/itineraries', icon: MapIcon, perm: 'console_itineraries_view' },
          { id: 'trip-types', label: 'Trip Types', to: '/trip-types', icon: FilterAltIcon, perm: 'console_trip_types_view' },
          { id: 'regions', label: 'Regions', to: '/regions', icon: PublicIcon, perm: 'console_regions_view' },
          { id: 'pre-trip-items', label: 'Trip Items', to: '/pre-trip-items', icon: PlaylistAddCheckIcon, perm: 'console_pretrip_view' },
        ],
      },
      {
        id: 'activities',
        label: 'Activities',
        icon: LocalActivityIcon,
        children: [
          { id: 'activities-catalog', label: 'Activities', to: '/activities', icon: LocalActivityIcon, perm: 'console_activities_view' },
          { id: 'activity-categories', label: 'Categories', to: '/activity-categories', icon: CategoryIcon, perm: 'console_activities_view' },
        ],
      },
      {
        id: 'fleet',
        label: 'Fleet',
        icon: DirectionsBusIcon,
        children: [
          { id: 'fleet-vehicles', label: 'Vehicles', to: '/vehicles', icon: AirportShuttleIcon, perm: 'console_fleet_view' },
          { id: 'fleet-bus-stops', label: 'Bus stops', to: '/bus-stops', icon: DirectionsBusIcon, perm: 'console_fleet_view' },
          { id: 'fleet-line-sets', label: 'Line sets', to: '/line-sets', icon: RouteIcon, perm: 'console_fleet_view' },
        ],
      },
      {
        id: 'distribution',
        label: 'Distribution',
        icon: StorefrontIcon,
        children: [
          { id: 'otas', label: 'OTAs', to: '/otas', icon: TravelExploreIcon, perm: 'console_ota_view' },
          { id: 'launch-policies', label: 'Launch Policies', to: '/launch-policies', icon: RocketLaunchIcon, perm: 'console_launch_policy_view' },
        ],
      },
    ],
  },
  {
    id: 'tags',
    label: 'Tags',
    entries: [
      { id: 'tags-all', label: 'Tags', to: '/tags', icon: LocalOfferIcon, perm: 'console_tags_view' },
      { id: 'tag-categories', label: 'Tag Categories', to: '/tag-categories', icon: CategoryIcon, perm: 'console_tag_categories_view' },
      { id: 'tag-schemas', label: 'Tag Schemas', to: '/tag-schemas', icon: SchemaIcon, perm: 'console_tag_schemas_view' },
      { id: 'tag-name-aliases', label: 'Tag Name Aliases', to: '/tag-name-aliases', icon: SpellcheckIcon, perm: 'console_tag_aliases_view' },
      { id: 'tag-canonicals', label: 'Canonicals', to: '/tag-canonicals', icon: SpellcheckIcon, perm: 'console_tag_aliases_view' },
      { id: 'translation-glossary', label: 'Translation Glossary', to: '/translation-glossary', icon: GTranslateIcon, perm: 'console_translation_glossary_view' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    entries: [
      { id: 'bookings', label: 'Bookings', to: '/bookings', icon: BookOnlineIcon, perm: 'console_bookings_view' },
      { id: 'orders', label: 'Orders', to: '/orders', icon: ReceiptLongIcon, perm: 'console_orders_view' },
      { id: 'delivery-logs', label: 'Delivery Logs', to: '/delivery-logs', icon: ForwardToInboxIcon, perm: 'console_event_delivery_view' },
      { id: 'import-runs', label: 'Imports', to: '/import-runs', icon: DriveFolderUploadIcon, perm: 'console_import_runs_view' },
      { id: 'api-requests', label: 'API Requests', to: '/api-requests', icon: WebhookIcon, perm: 'console_api_requests_view' },
      { id: 'subscriptions', label: 'Webhook subscriptions', to: '/subscriptions', icon: NotificationsActiveIcon, perm: 'console_restech_view' },
      { id: 'api-credentials', label: 'API Credentials', to: '/api-credentials', icon: VpnKeyIcon, perm: 'console_api_credentials_view' },
      { id: 'partners', label: 'Partners', to: '/partners', icon: HandshakeIcon, perm: 'console_partners_view' },
      { id: 'external-access', label: 'External Access', to: '/external-access', icon: QueryStatsIcon, perm: 'console_external_access_view' },
      { id: 'ext-ids', label: 'External ID Mappings', to: '/ext-ids', icon: LinkIcon, perm: 'console_ext_ids_view' },
      {
        id: 'data-sync',
        label: 'Data Sync',
        icon: SyncIcon,
        children: [
          { id: 'sync-products', label: 'Product Sync', to: '/sync/products', icon: Inventory2Icon, perm: 'console_bg_sync_products' },
          { id: 'sync-orders', label: 'Order Sync', to: '/sync/orders', icon: ShoppingCartCheckoutIcon, perm: 'console_bg_sync_orders' },
          { id: 'sync-bookings', label: 'Booking Sync', to: '/sync/bookings', icon: EventRepeatIcon, perm: 'console_bg_sync_bookings' },
          { id: 'ai-import', label: 'AI Import', to: '/ai-import', icon: AutoAwesomeIcon, perm: 'console_bg_ai_import' },
          { id: 'sync-aux-info', label: 'Aux Info Sync', to: '/sync/aux-info', icon: ManageSearchIcon, perm: 'console_bg_aux_sync' },
        ],
      },
      {
        // Onboarding checklists — operational work (was its own single-group section).
        id: 'preflight',
        label: 'Preflight',
        icon: ChecklistIcon,
        children: [
          { id: 'preflight-items', label: 'Items', to: '/preflight-items', icon: PlaylistAddCheckIcon, perm: 'console_preflight_view' },
          { id: 'preflight-templates', label: 'Preflight Templates', to: '/preflight-templates', icon: FactCheckIcon, perm: 'console_preflight_view' },
          { id: 'preflight-groups', label: 'Checklist Groups', to: '/preflight-groups', icon: WorkspacesIcon, perm: 'console_preflight_view' },
          { id: 'preflight-user-groups', label: 'User Groups', to: '/preflight-user-groups', icon: PeopleIcon, perm: 'console_preflight_view' },
          { id: 'tag-category-sets', label: 'Tag Category Sets', to: '/tag-category-sets', icon: SellIcon, perm: 'console_preflight_view' },
        ],
      },
      { id: 'blacklists', label: 'Blacklists', to: '/blacklists', icon: BlockIcon, perm: 'console_blacklist_view' },
      { id: 'bgtasks', label: 'Background Tasks', to: '/bgtasks', icon: TaskAltIcon },
      { id: 'audit', label: 'Audit Log', to: '/audit', icon: HistoryIcon, perm: 'console_audit_view' },
      { id: 'changes', label: 'Changes', to: '/changes', icon: ManageHistoryIcon, perm: 'console_audit_view' },
      { id: 'ai-spend', label: 'AI Spend', to: '/ai-spend', icon: PaidIcon, perm: 'console_ai_spend_view' },
      { id: 'ai-changes', label: 'AI Changes', to: '/ai-changes', icon: PublishedWithChangesIcon, perm: 'console_ai_changes_view' },
      {
        // Clients' AI over MCP: what it escalates to us and what ops tell it (client MCP plan).
        id: 'client-mcp',
        label: 'Client MCP',
        icon: HubIcon,
        children: [
          { id: 'support-tickets', label: 'Support Tickets', to: '/support-tickets', icon: SupportAgentIcon, perm: 'console_support_tickets_view' },
          { id: 'platform-notices', label: 'Platform Notices', to: '/platform-notices', icon: CampaignIcon, perm: 'console_platform_notices_manage' },
          { id: 'mcp-limit-hits', label: 'Limit Hits', to: '/mcp-limit-hits', icon: SpeedIcon, perm: 'console_mcp_limits_view' },
          { id: 'knowledge-gaps', label: 'Knowledge Gaps', to: '/knowledge-gaps', icon: PsychologyAltIcon, perm: 'console_knowledge_gaps_view' },
          { id: 'mcp-diagnose-runs', label: 'Diagnose Reviews', to: '/mcp-diagnose-runs', icon: RateReviewIcon, perm: 'console_entity_ai' },
        ],
      },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    entries: [
      { id: 'notifications-overview', label: 'Overview', to: '/notifications/overview', icon: TimelineIcon, perm: 'console_notifications_view' },
      { id: 'notification-anatomy', label: 'Anatomy', to: '/notifications/anatomy', icon: PolylineIcon, perm: 'console_notifications_view' },
      { id: 'notifications-grid', label: 'Notifications', to: '/notifications', end: true, icon: EditNotificationsIcon, perm: 'console_notifications_view' },
      { id: 'notification-rulesets', label: 'Rulesets', to: '/notifications/rulesets', icon: RuleIcon, perm: 'console_notification_rulesets_view' },
      { id: 'notification-blocks', label: 'Blocks', to: '/notifications/blocks', icon: TuneIcon, perm: 'console_notification_blocks_view' },
      { id: 'block-lists', label: 'Block Lists', to: '/notifications/block-lists', icon: PlaylistPlayIcon, perm: 'console_block_lists_view' },
      { id: 'canned-notifications', label: 'Canned Messages', to: '/notifications/canned', icon: AutoAwesomeMotionIcon, perm: 'console_canned_notifications_view' },
      { id: 'notification-features', label: 'Feature Flags', to: '/notifications/features', icon: ToggleOnIcon, perm: 'console_notification_features_view' },
      { id: 'reseller-policies', label: 'Reseller Policies', to: '/notifications/reseller-policies', icon: ToggleOnIcon, perm: 'console_notification_features_view' },
      { id: 'notification-themes', label: 'Themes', to: '/notifications/themes', icon: PaletteIcon, perm: 'console_notification_themes_view' },
      { id: 'design-imports', label: 'Design Imports', to: '/notifications/design-imports', icon: DesignServicesIcon, perm: 'console_composition_view' },
      { id: 'language-policies', label: 'Language Policies', to: '/notifications/language-policies', icon: TranslateIcon, perm: 'console_language_policies_view' },
      { id: 'language-aliases', label: 'Language Aliases', to: '/notifications/language-aliases', icon: GTranslateIcon, perm: 'console_language_aliases_view' },
      { id: 'find-replace', label: 'Find & Replace', to: '/notifications/find-replace', icon: FindReplaceIcon, perm: 'console_content_search' },
      { id: 'notification-test-render', label: 'Test Render', to: '/notifications/test-render', icon: PreviewIcon, perm: 'console_notification_blocks_view' },
      { id: 'notification-ai-agent', label: 'AI Agent', to: '/notifications/ai-agent', icon: SmartToyIcon, perm: 'console_notification_mcp_read' },
    ],
  },
];

type FilteredSection = Section & { entries: Entry[] };

/** The section + group holding the active route (both null when the path — e.g.
 *  the dashboard — isn't in the nav). Drives which accordion opens on navigation. */
function locate(sections: FilteredSection[], pathname: string): {
  sectionId: string | null;
  groupId: string | null;
} {
  for (const s of sections) {
    for (const e of s.entries) {
      if (isGroup(e)) {
        if (e.children.some((c) => pathname.startsWith(c.to))) return { sectionId: s.id, groupId: e.id };
      } else if (pathname.startsWith(e.to)) {
        return { sectionId: s.id, groupId: null };
      }
    }
  }
  return { sectionId: null, groupId: null };
}

/** Nav drawer chrome — the console's identity: deep navy rail (the AppBar
 *  color, so the chrome reads as one piece), light text, and a signal-red
 *  active bar (`theme.palette.secondary.main`). Apply ONLY to the AppShell
 *  nav drawer — content drawers (session details, contacts, sync mapping)
 *  stay light. */
export const NAV_DRAWER_PAPER_SX = {
  bgcolor: 'primary.main',
  color: 'rgba(255,255,255,0.85)',
  borderRight: 'none',
  '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.65)' },
} as const;

interface SidebarMenuProps {
  /** Icon-only rail (desktop collapsed drawer). */
  mini?: boolean;
  /** Mini rail: a group icon was clicked — re-open the full drawer (the menu
   *  expands that group's section + the group itself). */
  onExpandGroup?: (groupId: string) => void;
  /** Called after navigating via a leaf item (the mobile drawer closes). */
  onNavigate?: () => void;
}

export function SidebarMenu({ mini = false, onExpandGroup, onNavigate }: SidebarMenuProps) {
  const { hasPerm } = usePermissions();
  const location = useLocation();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Reduced motion: collapse instantly (no height animation). Otherwise `auto`.
  const collapseTimeout = prefersReducedMotion ? 0 : 'auto';

  const visible = (item: Item) => !item.perm || hasPerm(item.perm);

  // Permission-filter each section's entries; drop empty groups/sections.
  const sections = useMemo<FilteredSection[]>(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        entries: section.entries
          .map((e) => (isGroup(e) ? { ...e, children: e.children.filter(visible) } : e))
          .filter((e) => (isGroup(e) ? e.children.length > 0 : visible(e))),
      })).filter((s) => s.entries.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasPerm],
  );

  // The section + group holding the active route (recomputed as perms load).
  const active = useMemo(
    () => locate(sections, location.pathname),
    [sections, location.pathname],
  );

  // Single-open accordion state: one section + one group open at a time. Seed
  // from the active route so a deep link lands with the right section unfurled.
  const [openSection, setOpenSection] = useState<string | null>(active.sectionId);
  const [openGroup, setOpenGroup] = useState<string | null>(active.groupId);

  // Re-open the active section/group ONLY when the route actually changes — never
  // on an incidental re-render (an unstable `hasPerm` or a parent render must not
  // snap a manually-opened section shut). A route with no nav match keeps the
  // user's current choice. This is the single-open accordion's height ceiling.
  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    if (active.sectionId) {
      setOpenSection(active.sectionId);
      setOpenGroup(active.groupId);
    }
  }, [location.pathname, active]);

  const groupContainsActive = (group: Group) =>
    group.children.some((c) => location.pathname.startsWith(c.to));
  const sectionContainsActive = (section: FilteredSection) =>
    section.entries.some((e) =>
      isGroup(e) ? groupContainsActive(e) : location.pathname.startsWith(e.to),
    );

  const renderLeaf = (item: Item, nested = false) => {
    if (mini) {
      return (
        <Tooltip key={item.id} title={item.label} placement="right">
          <ListItemButton
            component={NavLink}
            to={item.to}
            {...(item.end ? { end: true } : {})}
            onClick={onNavigate}
            aria-label={item.label}
            sx={{ ...miniButtonStyle, ...navLinkStyle }}
          >
            <ListItemIcon sx={miniIconStyle}><item.icon /></ListItemIcon>
          </ListItemButton>
        </Tooltip>
      );
    }
    return (
      <ListItemButton
        key={item.id}
        component={NavLink}
        to={item.to}
        {...(item.end ? { end: true } : {})}
        onClick={onNavigate}
        sx={{ ...compactRowStyle, ...navLinkStyle, ...(nested ? { pl: 3.5 } : {}) }}
      >
        <ListItemIcon sx={compactIconStyle}><item.icon /></ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItemButton>
    );
  };

  const renderGroup = (group: Group, sectionId: string) => {
    if (mini) {
      // The rail shows the group icon only; clicking it brings the full drawer
      // back with the group's section + the group itself expanded.
      return (
        <Tooltip key={group.id} title={group.label} placement="right">
          <ListItemButton
            onClick={() => {
              setOpenSection(sectionId);
              setOpenGroup(group.id);
              onExpandGroup?.(group.id);
            }}
            aria-label={group.label}
            sx={{
              ...miniButtonStyle,
              ...hoverFocusStyle,
              ...(groupContainsActive(group) ? activeGroupStyle : {}),
            }}
          >
            <ListItemIcon sx={miniIconStyle}><group.icon /></ListItemIcon>
          </ListItemButton>
        </Tooltip>
      );
    }
    const expanded = openGroup === group.id;
    const listId = `nav-group-${group.id}`;
    return (
      <Box key={group.id}>
        <ListItemButton
          onClick={() => setOpenGroup(expanded ? null : group.id)}
          aria-expanded={expanded}
          aria-controls={listId}
          sx={{
            ...compactRowStyle,
            ...hoverFocusStyle,
            ...(groupContainsActive(group) && !expanded ? activeGroupStyle : {}),
          }}
        >
          <ListItemIcon sx={compactIconStyle}><group.icon /></ListItemIcon>
          <ListItemText primary={group.label} />
          {expanded ? <ExpandLessIcon fontSize="small" sx={chevronStyle} /> : <ExpandMoreIcon fontSize="small" sx={chevronStyle} />}
        </ListItemButton>
        <Collapse in={expanded} timeout={collapseTimeout} unmountOnExit>
          <List dense disablePadding id={listId}>
            {group.children.map((c) => renderLeaf(c, true))}
          </List>
        </Collapse>
      </Box>
    );
  };

  if (mini) {
    return (
      <Box component="nav" aria-label="Primary">
        {sections.map((section, i) => (
          <Box key={section.id}>
            {i > 0 && <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.12)' }} />}
            <List dense disablePadding>
              {section.entries.map((e) => (isGroup(e) ? renderGroup(e, section.id) : renderLeaf(e)))}
            </List>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box component="nav" aria-label="Primary">
      {sections.map((section) => {
        const expanded = openSection === section.id;
        const headerId = `nav-section-${section.id}`;
        const listId = `${headerId}-list`;
        return (
          <Box key={section.id}>
            <ListItemButton
              id={headerId}
              onClick={() => setOpenSection(expanded ? null : section.id)}
              aria-expanded={expanded}
              aria-controls={listId}
              sx={{
                ...sectionHeaderStyle,
                ...(sectionContainsActive(section) && !expanded ? sectionActiveStyle : {}),
              }}
            >
              <ListItemText primary={section.label} primaryTypographyProps={{ sx: sectionLabelStyle }} />
              {expanded ? <ExpandLessIcon fontSize="small" sx={chevronStyle} /> : <ExpandMoreIcon fontSize="small" sx={chevronStyle} />}
            </ListItemButton>
            <Collapse in={expanded} timeout={collapseTimeout} unmountOnExit>
              <List dense disablePadding id={listId} aria-labelledby={headerId}>
                {section.entries.map((e) => (isGroup(e) ? renderGroup(e, section.id) : renderLeaf(e)))}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

// The default MUI hover/selected/focus tints are black-alpha — invisible on
// the navy rail; every interactive style below carries explicit white-alpha
// states. The 3px inset bar is theme.palette.secondary.main.
const ACTIVE_BAR = 'inset 3px 0 0 0 #fa3f33';

const hoverFocusStyle = {
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
  '&.Mui-focusVisible': { backgroundColor: 'rgba(255,255,255,0.15)' },
};

// Compact row geometry — ~34px vs MUI's stock ~40px dense item, applied to nav
// rows only (not a global MuiListItemButton override, which would touch every
// list in the app). Tighter icon gutter reclaims horizontal space too.
const compactRowStyle = { minHeight: 34, py: 0.25 };
const compactIconStyle = { minWidth: 32 };

const navLinkStyle = {
  ...hoverFocusStyle,
  '&.active': {
    backgroundColor: 'rgba(255,255,255,0.12)',
    boxShadow: ACTIVE_BAR,
    '& .MuiListItemIcon-root': { color: '#fff' },
    '& .MuiListItemText-primary': { color: '#fff', fontWeight: 600 },
  },
};

// A collapsed group (or a mini-rail group icon) containing the active route
// still hints at where you are.
const activeGroupStyle = {
  boxShadow: ACTIVE_BAR,
  '& .MuiListItemIcon-root': { color: '#fff' },
  '& .MuiListItemText-primary': { color: '#fff', fontWeight: 600 },
};

// Icon-only rail items: centered icon, label lives in the tooltip.
const miniButtonStyle = { justifyContent: 'center', px: 2.5, minHeight: 40 };
const miniIconStyle = { minWidth: 0, justifyContent: 'center' };

// Section header — now a clickable accordion toggle. Keeps the tracked-uppercase
// look (DESIGN.md section-header) but as an interactive row with white-alpha
// states. Color is set via the descendant selector so the active variant can
// brighten it without dropping the label's typography.
const sectionHeaderStyle = {
  minHeight: 32,
  py: 0.25,
  ...hoverFocusStyle,
  '& .MuiListItemText-primary': { color: 'rgba(255,255,255,0.5)' },
};
const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
};
const sectionActiveStyle = {
  boxShadow: ACTIVE_BAR,
  '& .MuiListItemText-primary': { color: 'rgba(255,255,255,0.9)' },
};

const chevronStyle = { color: 'rgba(255,255,255,0.5)' };
